import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import moment from "moment";

import {
  ModalBlur,
  ModalContainer,
  ModalMainText,
  ModalSecundaryText,
  ModalButtonsContainer,
} from "../../../components/shared/components/generalModal/GeneralModal.style";
import Button from "../../../components/shared/components/button/Button.style";
import ButtonComponent from "../../../components/shared/components/button/Button.component";
import DatePicker from "../../../components/datepicker/DatePicker.component";
import TimePicker from "../../../components/timePicker/TimePicker.component";
import { getCurrentDateTimeStrings } from "../../../tools/date-utils";
import { toastErrorConfig } from "../../../components/shared/constants/toaster-config";

/**
 * Splits a sendAt value into date ("YYYY-MM-DD") and time ("HH:mm") strings.
 * Falls back to the current date/time when the value is missing or already in the past.
 *
 * @param {string} sendAt - ISO-like datetime string, may be undefined.
 * @returns {{ date: string, time: string }}
 */
const toDateTimeStrings = (sendAt) => {
  const parsed = moment(sendAt);
  if (!sendAt || !parsed.isValid() || parsed.isBefore(moment())) {
    return getCurrentDateTimeStrings();
  }
  return { date: parsed.format("YYYY-MM-DD"), time: parsed.format("HH:mm") };
};

/**
 * Modal that reschedules a SCHEDULED campaign to a new send time.
 *
 * @param {Object}   props
 * @param {boolean}  props.show           - Whether the modal is visible.
 * @param {boolean}  props.isUpdating     - Loading state forwarded to the confirm button.
 * @param {string}   [props.currentSendAt] - Campaign's current sendAt, used to prefill the pickers.
 * @param {Function} props.onConfirm      - Called with `{ sendAt: string }`. sendAt format: "YYYY-MM-DDTHH:mm:ss".
 * @param {Function} props.onClose        - Called when the user dismisses the modal.
 */
const RescheduleCampaignModal = ({
  show,
  isUpdating,
  currentSendAt,
  onConfirm,
  onClose,
}) => {
  const initial = toDateTimeStrings(currentSendAt);

  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);

  // Reset the pickers to the campaign's current schedule whenever the modal opens.
  useEffect(() => {
    if (show) {
      const { date: d, time: t } = toDateTimeStrings(currentSendAt);
      setDate(d);
      setTime(t);
    }
  }, [show, currentSendAt]);

  if (!show) return null;

  const handleConfirm = () => {
    const chosen = new Date(`${date}T${time}`);
    if (chosen <= new Date()) {
      toast.error("La fecha debe ser futura", toastErrorConfig);
      return;
    }

    const sendAt = `${date}T${time}:00`;
    onConfirm({ sendAt });
  };

  const { date: today } = getCurrentDateTimeStrings();

  return ReactDOM.createPortal(
    <>
      <ModalBlur fixed />
      <ModalContainer type="general" fixed>
        <ModalMainText hasMarginTop hasMarginBottom>
          Reprogramar campaña
        </ModalMainText>
        <ModalSecundaryText hasMarginTop>
          Elige una nueva fecha y hora de envío para esta campaña.
        </ModalSecundaryText>

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: "15px",
            margin: "1rem 2rem",
          }}
        >
          <DatePicker
            label="Fecha"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            min={today}
          />
          <TimePicker
            label="Hora"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>

        <ModalButtonsContainer>
          <Button secondary onClick={onClose} type="button">
            Volver
          </Button>
          <ButtonComponent
            onClick={handleConfirm}
            loading={isUpdating}
            disabled={isUpdating}
            text="Reprogramar"
          />
        </ModalButtonsContainer>
      </ModalContainer>
    </>,
    document.body
  );
};

export default RescheduleCampaignModal;
